import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { ChatbotAuthProvider } from "./ChatbotAuthContext";
import { BotProvider } from "./BotContext";
import ChatWidget from "./ChatWidget";
import Login from "./Login";
import ResetPassword from "./ResetPassword";
import Signup from "./Signup";
import SuperAdminLogin from "./SuperAdminLogin";
import SuperAdminSignup from "./SuperAdminSignup";
import SuperAdminForgotPassword, { SuperAdminResetPassword } from "./SuperAdminForgotPassword";
import AdminPanel from "./AdminPanel";
import SuperAdminPanel from "./SuperAdminPanel";
import WidgetMode from "./WidgetMode";
import { getRouterBasename } from "./config";

/**
 * With homepage "/chatbot", embed iframes load /chatbot/?bot_id=... In development,
 * Router basename is "" so that path does not match "/" and the catch-all Navigate
 * sent users to "/" without the query string — widget then fell back to MAIN / default-bot.
 */
function ChatbotPublicPathRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/", search: location.search, hash: location.hash }}
      replace
    />
  );
}

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, logout, authBootstrapped } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState(null);
  const location = useLocation();
  const hasStoredToken =
    typeof window !== 'undefined' && !!localStorage.getItem('authToken');

  // Handle role mismatch in useEffect to avoid state update during render
  useEffect(() => {
    if (requiredRole && user?.role && user.role !== requiredRole) {
      // User is trying to access wrong panel - log them out and redirect to correct login
      const currentPath = location.pathname;

      // Log out the user to ensure complete separation
      logout();

      // Set redirect path
      if (currentPath.includes('super-admin')) {
        // User is org_admin trying to access super-admin panel
        setShouldRedirect('/super-admin-login');
      } else {
        // User is super_admin trying to access org-admin panel
        setShouldRedirect('/login');
      }
    }
  }, [requiredRole, user, logout, location.pathname]);

  if (!authBootstrapped) {
    return null;
  }

  if (!isAuthenticated) {
    if (hasStoredToken) {
      return null;
    }
    const currentPath = location.pathname;
    if (currentPath.includes('super-admin')) {
      return <Navigate to="/super-admin-login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Wait for user data to load if authenticated but user is null
  if (isAuthenticated && !user) {
    return null; // or a loading spinner
  }

  // Redirect if role mismatch was detected
  if (shouldRedirect) {
    return <Navigate to={shouldRedirect} replace />;
  }

  // Check role if required - if mismatch, useEffect will handle it
  if (requiredRole && user?.role && user.role !== requiredRole) {
    return null;
  }

  return children;
};

// Public Route Component (redirect based on role if already logged in)
const PublicRoute = ({ children, allowAccessWhenAuthenticated = false }) => {
  const { isAuthenticated, user, authBootstrapped } = useAuth();

  if (allowAccessWhenAuthenticated) {
    return children;
  }

  if (!authBootstrapped) {
    return null;
  }

  if (isAuthenticated && user) {
    // Redirect based on user role
    if (user.role === 'super_admin') {
      return <Navigate to="/super-admin" replace />;
    } else {
      return <Navigate to="/admin" replace />;
    }
  }
  return children;
};

// Widget Mode Handler - Manages body/html/root classes for widget mode
const WidgetModeHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const rootElement = document.getElementById('root');

    if (location.pathname.startsWith('/widget') || location.pathname === '/iframe-mode-only') {
      document.body.classList.add('widget-mode-active');
      document.documentElement.classList.add('widget-mode-active');
      if (rootElement) {
        rootElement.classList.add('widget-mode-active');
      }
    } else {
      document.body.classList.remove('widget-mode-active');
      document.documentElement.classList.remove('widget-mode-active');
      if (rootElement) {
        rootElement.classList.remove('widget-mode-active');
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('widget-mode-active');
      document.documentElement.classList.remove('widget-mode-active');
      if (rootElement) {
        rootElement.classList.remove('widget-mode-active');
      }
    };
  }, [location.pathname]);

  return null;
};

// Main App Content
const AppContent = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div>
      <WidgetModeHandler />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />
        <Route
          path="/super-admin-login"
          element={
            <PublicRoute allowAccessWhenAuthenticated={true}>
              <SuperAdminLogin />
            </PublicRoute>
          }
        />
        <Route
          path="/super-admin-signup"
          element={
            <PublicRoute allowAccessWhenAuthenticated={true}>
              <SuperAdminSignup />
            </PublicRoute>
          }
        />
        <Route
          path="/super-admin-forgot-password"
          element={
            <PublicRoute allowAccessWhenAuthenticated={true}>
              <SuperAdminForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/super-admin-reset-password"
          element={
            <PublicRoute allowAccessWhenAuthenticated={true}>
              <SuperAdminResetPassword />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="org_admin">
              <BotProvider>
                <AdminPanel />
              </BotProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <SuperAdminPanel />
            </ProtectedRoute>
          }
        />

        {/* Widget Mode Route - supports /widget/:botId */}
        <Route
          path="/widget/:botId?"
          element={<WidgetMode />}
        />
        <Route
          path="/widget"
          element={<WidgetMode />}
        />

        {/* Default Route - Customer Chat */}
        <Route
          path="/"
          element={
            <ChatbotAuthProvider>
              <div style={{ height: "100vh", background: "white" }}>
                <ChatWidget />
              </div>
            </ChatbotAuthProvider>
          }
        />

        {/* Same as / when static hosting uses /chatbot (dev server + widget iframe URL) */}
        <Route path="/chatbot" element={<ChatbotPublicPathRedirect />} />
        <Route path="/chatbot/" element={<ChatbotPublicPathRedirect />} />

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

// Main App Component
function App() {
  const basename = getRouterBasename();

  return (
    <AuthProvider>
      <Router basename={basename}>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
// Build: Tue Jan 27 17:02:10 CET 2026
// HashBreaker: Tue Jan 27 17:05:10 CET 2026
// Version: V12-1769530197
// Recovery: 1769531032
/* Refresh: 1769531422 */
